import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TrendingUp, Ticket, Package, Users, Trash2, Plus, CalendarDays, Store, Check, X, RotateCcw, Mail, Phone } from "lucide-react";
import { api, currencySymbol, logError, PLACEHOLDER_IMG } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATS = ["boat-tours", "sightseeing", "watersports", "adventure", "family", "nightlife"];
const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseDate = (s) => new Date(s + "T00:00:00");
const bStyle = {
  paid: "bg-emerald-100 text-emerald-700", confirmed: "bg-emerald-100 text-emerald-700",
  cash_on_arrival: "bg-blue-100 text-blue-700", pending: "bg-amber-100 text-amber-700",
  refunded: "bg-slate-200 text-slate-600", cancelled: "bg-red-100 text-red-600",
};

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const isStaff = user && (user.role === "admin" || user.role === "vendor");
  const isAdmin = user && user.role === "admin";

  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [availOpen, setAvailOpen] = useState(false);
  const [availActivity, setAvailActivity] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [capacity, setCapacity] = useState("");
  const [vendorForm, setVendorForm] = useState({ name: "", email: "", phone: "" });

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => logError("stats", e));
    api.get("/admin/activities").then((r) => setActivities(r.data)).catch((e) => logError("activities", e));
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch((e) => logError("bookings", e));
    if (user?.role === "admin") api.get("/admin/vendors").then((r) => setVendors(r.data)).catch((e) => logError("vendors", e));
  }, [user]);

  useEffect(() => {
    if (loading) return;
    if (!user || !(user.role === "admin" || user.role === "vendor")) { navigate("/"); return; }
    loadAll();
  }, [user, loading, navigate, loadAll]);

  const emptyForm = { title: "", category: "boat-tours", price: 30, duration: "", short_description: "", description: "",
    pickup_info: "", images: "", inclusions: "", max_guests: 20, instant_booking: true, featured: false, vendor_id: "",
    location: { name: "Alanya", lat: 36.5444, lng: 31.9959, address: "Alanya" } };
  const [form, setForm] = useState(emptyForm);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a.id);
    setForm({ ...a, images: a.images.join(", "), inclusions: a.inclusions.join(", "), vendor_id: a.vendor_id || "" });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      price: parseFloat(form.price),
      max_guests: parseInt(form.max_guests),
      images: form.images.split(",").map((s) => s.trim()).filter(Boolean),
      inclusions: form.inclusions.split(",").map((s) => s.trim()).filter(Boolean),
      vendor_id: form.vendor_id || null,
      location: form.location,
    };
    try {
      if (editing) await api.put(`/activities/${editing}`, payload);
      else await api.post("/activities", payload);
      toast.success(editing ? "Activity updated" : "Activity created");
      setOpen(false); loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this activity?")) return;
    try { await api.delete(`/activities/${id}`); toast.success("Deleted"); loadAll(); }
    catch (e) { toast.error("Delete failed"); }
  };

  // Availability
  const openAvail = (a) => {
    setAvailActivity(a);
    setBlockedDates((a.blocked_dates || []).map(parseDate));
    setCapacity(a.daily_capacity ?? "");
    setAvailOpen(true);
  };
  const saveAvail = async () => {
    try {
      await api.put(`/activities/${availActivity.id}/availability`, {
        blocked_dates: blockedDates.map(fmtDate),
        daily_capacity: capacity === "" ? null : parseInt(capacity),
      });
      toast.success("Availability updated");
      setAvailOpen(false); loadAll();
    } catch (e) { toast.error("Could not save availability"); }
  };

  // Booking controls
  const bookingAction = async (id, action) => {
    try {
      await api.post(`/admin/bookings/${id}/${action}`);
      toast.success(`Booking ${action === "refund" ? "refunded" : action + "ed"}`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  // Vendors
  const addVendor = async () => {
    if (!vendorForm.name || !vendorForm.email) return toast.error("Name and email required");
    try {
      await api.post("/admin/vendors", vendorForm);
      toast.success("Vendor added");
      setVendorForm({ name: "", email: "", phone: "" });
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add vendor"); }
  };
  const delVendor = async (id) => {
    if (!window.confirm("Remove this vendor? Their listings will become unassigned.")) return;
    try { await api.delete(`/admin/vendors/${id}`); toast.success("Vendor removed"); loadAll(); }
    catch (e) { toast.error("Could not remove vendor"); }
  };

  if (!isStaff) return null;

  const cards = [
    { icon: TrendingUp, label: "Revenue", value: `€${stats?.revenue ?? 0}` },
    { icon: Ticket, label: "Bookings", value: stats?.total_bookings ?? 0 },
    { icon: Package, label: "Activities", value: stats?.total_activities ?? 0 },
    ...(isAdmin ? [{ icon: Users, label: "Users", value: stats?.total_users ?? 0 }] : []),
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-display text-3xl font-bold tracking-tight mb-1">{isAdmin ? "Admin Panel" : "Vendor Dashboard"}</h1>
      <p className="text-sm text-muted-foreground mb-6">{isAdmin ? "Manage listings, bookings, availability and partners." : "Manage your listings, availability and bookings."}</p>

      <div className={`grid grid-cols-2 ${isAdmin ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-4 mb-8`}>
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl bg-white border border-black/5 soft-shadow p-5" data-testid={`stat-${c.label.toLowerCase()}`}>
            <c.icon className="h-5 w-5 text-primary mb-2" />
            <p className="font-display text-2xl font-bold">{c.value}</p>
            <p className="text-sm text-muted-foreground">{c.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="listings">
        <TabsList>
          <TabsTrigger value="listings" data-testid="admin-tab-listings">Listings</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings</TabsTrigger>
          {isAdmin && <TabsTrigger value="vendors" data-testid="admin-tab-vendors">Vendors</TabsTrigger>}
        </TabsList>

        {/* Listings */}
        <TabsContent value="listings" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button data-testid="new-activity-btn" onClick={openNew} className="rounded-full gap-1"><Plus className="h-4 w-4" /> New activity</Button>
          </div>
          <div className="rounded-2xl bg-white border border-black/5 soft-shadow overflow-hidden divide-y divide-black/5">
            {activities.length === 0 && <p className="p-6 text-sm text-muted-foreground">No listings yet.</p>}
            {activities.map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-3" data-testid={`admin-activity-${a.id}`}>
                <img src={a.images[0] || PLACEHOLDER_IMG} alt="" className="h-12 w-12 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{a.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {a.category} · {currencySymbol(a.currency)}{a.price}
                    {(a.blocked_dates?.length > 0 || a.daily_capacity) && <span className="ml-2 text-primary">· availability set</span>}
                  </p>
                </div>
                <Button variant="ghost" size="sm" className="rounded-full gap-1 text-primary" data-testid={`avail-${a.id}`} onClick={() => openAvail(a)}>
                  <CalendarDays className="h-4 w-4" /> <span className="hidden sm:inline">Availability</span>
                </Button>
                <Button variant="outline" size="sm" className="rounded-full" data-testid={`edit-${a.id}`} onClick={() => openEdit(a)}>Edit</Button>
                <button data-testid={`delete-${a.id}`} onClick={() => del(a.id)} className="grid place-items-center h-9 w-9 rounded-full hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Bookings */}
        <TabsContent value="bookings" className="mt-6">
          <div className="rounded-2xl bg-white border border-black/5 soft-shadow overflow-hidden divide-y divide-black/5">
            {bookings.length === 0 && <p className="p-6 text-sm text-muted-foreground">No bookings yet.</p>}
            {bookings.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center gap-3 p-3 text-sm" data-testid={`admin-booking-${b.id}`}>
                <div className="flex-1 min-w-[180px]">
                  <p className="font-semibold truncate">{b.activity_title}</p>
                  <p className="text-xs text-muted-foreground">{b.customer_email} · {b.date} {b.time} · {b.guests} guest(s)</p>
                </div>
                <span className="font-semibold">{currencySymbol(b.currency)}{b.total_price}</span>
                {(() => {
                  const lbl = ["cancelled", "refunded", "confirmed"].includes(b.status) ? b.status : b.payment_status;
                  return (
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${bStyle[lbl] || "bg-slate-100 text-slate-600"}`} data-testid={`admin-booking-status-${b.id}`}>
                      {lbl}
                    </span>
                  );
                })()}
                <div className="flex items-center gap-1">
                  {b.status !== "confirmed" && b.status !== "cancelled" && b.status !== "refunded" && (
                    <button data-testid={`confirm-${b.id}`} onClick={() => bookingAction(b.id, "confirm")} title="Confirm"
                      className="grid place-items-center h-8 w-8 rounded-full hover:bg-emerald-50 text-emerald-600"><Check className="h-4 w-4" /></button>
                  )}
                  {b.status !== "cancelled" && b.status !== "refunded" && (
                    <button data-testid={`cancel-${b.id}`} onClick={() => bookingAction(b.id, "cancel")} title="Cancel"
                      className="grid place-items-center h-8 w-8 rounded-full hover:bg-red-50 text-red-500"><X className="h-4 w-4" /></button>
                  )}
                  {b.payment_status === "paid" && (
                    <button data-testid={`refund-${b.id}`} onClick={() => bookingAction(b.id, "refund")} title="Refund"
                      className="grid place-items-center h-8 w-8 rounded-full hover:bg-slate-100 text-slate-600"><RotateCcw className="h-4 w-4" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Vendors */}
        {isAdmin && (
          <TabsContent value="vendors" className="mt-6">
            <div className="grid md:grid-cols-[1fr_320px] gap-6">
              <div className="rounded-2xl bg-white border border-black/5 soft-shadow overflow-hidden divide-y divide-black/5">
                {vendors.length === 0 && <p className="p-6 text-sm text-muted-foreground">No vendors yet. Add a tour operator to let them manage their own listings.</p>}
                {vendors.map((v) => (
                  <div key={v.id} className="flex items-center gap-3 p-3" data-testid={`vendor-${v.id}`}>
                    <span className="grid place-items-center h-10 w-10 rounded-full bg-primary/10 text-primary"><Store className="h-5 w-5" /></span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{v.name}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{v.email}</span>
                        {v.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{v.phone}</span>}
                      </p>
                    </div>
                    <button data-testid={`del-vendor-${v.id}`} onClick={() => delVendor(v.id)} className="grid place-items-center h-9 w-9 rounded-full hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl bg-white border border-black/5 soft-shadow p-5 h-fit">
                <h3 className="font-display font-semibold mb-3">Add vendor</h3>
                <div className="space-y-3">
                  <div><Label className="text-sm mb-1 block">Business name</Label><Input data-testid="vendor-name" value={vendorForm.name} onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })} /></div>
                  <div><Label className="text-sm mb-1 block">Login email (Google)</Label><Input data-testid="vendor-email" type="email" value={vendorForm.email} onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })} /></div>
                  <div><Label className="text-sm mb-1 block">Phone (optional)</Label><Input data-testid="vendor-phone" value={vendorForm.phone} onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })} /></div>
                  <Button data-testid="add-vendor-btn" onClick={addVendor} className="w-full rounded-full">Add vendor</Button>
                  <p className="text-xs text-muted-foreground">When this person signs in with Google using that email, they'll get their own vendor dashboard.</p>
                </div>
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Activity form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit activity" : "New activity"}</DialogTitle>
            <DialogDescription>Fill in the activity details. Comma-separated fields accept multiple values.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Title"><Input data-testid="form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Price (€)"><Input data-testid="form-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Duration"><Input data-testid="form-duration" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} /></Field>
              <Field label="Max guests"><Input data-testid="form-max-guests" type="number" value={form.max_guests} onChange={(e) => setForm({ ...form, max_guests: e.target.value })} /></Field>
            </div>
            {isAdmin && (
              <Field label="Vendor / operator">
                <Select value={form.vendor_id || "none"} onValueChange={(v) => setForm({ ...form, vendor_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="form-vendor"><SelectValue placeholder="Unassigned (managed by admin)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned (admin managed)</SelectItem>
                    {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Short description"><Input data-testid="form-short" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} /></Field>
            <Field label="Description"><Textarea data-testid="form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            <Field label="Pickup info"><Input data-testid="form-pickup" value={form.pickup_info} onChange={(e) => setForm({ ...form, pickup_info: e.target.value })} /></Field>
            <Field label="Image URLs (comma separated)"><Textarea data-testid="form-images" value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} /></Field>
            <Field label="Inclusions (comma separated)"><Input data-testid="form-inclusions" value={form.inclusions} onChange={(e) => setForm({ ...form, inclusions: e.target.value })} /></Field>
            <Button data-testid="save-activity-btn" onClick={save} className="w-full rounded-full">{editing ? "Update" : "Create"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Availability dialog */}
      <Dialog open={availOpen} onOpenChange={setAvailOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Availability — {availActivity?.title}</DialogTitle>
            <DialogDescription>Block out dates when this activity does not run, and set a daily capacity.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Daily capacity (max guests per day)">
              <Input data-testid="avail-capacity" type="number" min={1} placeholder={`Default: ${availActivity?.max_guests}`} value={capacity} onChange={(e) => setCapacity(e.target.value)} />
            </Field>
            <div>
              <Label className="text-sm mb-2 block">Blocked dates (tap to toggle)</Label>
              <div className="rounded-xl border border-black/10 grid place-items-center" data-testid="avail-calendar">
                <Calendar mode="multiple" selected={blockedDates} onSelect={(d) => setBlockedDates(d || [])} disabled={{ before: new Date() }} />
              </div>
              <p className="text-xs text-muted-foreground mt-2">{blockedDates.length} date(s) blocked</p>
            </div>
            <Button data-testid="save-avail-btn" onClick={saveAvail} className="w-full rounded-full">Save availability</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-sm mb-1 block">{label}</Label>{children}</div>
);
